<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, watch } from 'vue'
import ArrowRight from '~/assets/icon/arrow-right.svg'
import DownloadIcon from '~/assets/icon/store-download.svg'
import UserIcon from '~/assets/icon/user-1-O.svg'
import {
  getHomeStoreInitialData,
  getHomeStoreStatistics,
  type HomeStoreInitialPayload,
  type HomeStoreStatistics,
} from '~/api/home-store'
import { isChineseLocale } from '~/composables/useAppI18n'
import areaCodeCn from '~/assets/json/areaCodeCN.json'
import areaCodeEn from '~/assets/json/areaCodeEn.json'
import {
  ensureHomeStoreLocalePrefix,
  getHomeStoreCardColumnCount,
  getHomeStoreDeviceType,
  getHomeStoreStatIncrements,
  getNextHomeStoreBannerIndex,
  getPreviousHomeStoreBannerIndex,
  getVisibleHomeStoreApps,
  mergeHomeStorePayload,
  normalizeHomeStoreData,
  type HomeStoreApp,
  type HomeStoreLinkTarget,
  type HomeStoreStats,
} from '~/utils/homeStore'

const appStore = useAppStore()
const { locale, localeMeta, localePath, t } = useAppI18n()
const matomo = useMatomo()

const STORE_ORIGIN = 'https://store.wikibit.com'
/** 月度活跃排行数值图标。 */
const ACTIVE_RANKING_ICON =
  'https://resources1.interface003.com/web30/2026/09/16/359c78f3-5c52-4cd5-90d8-47ec6c22ab53.png'
/** 排名前三名沿用首页 Exchange 广告卡的排名徽标。 */
const DOWNLOAD_RANK_ICONS: Record<number, string> = {
  1: 'https://resources1.interface003.com/web30/2026/07/01/356b9063-bfcc-493f-a6db-4d5640cb2aa3.png',
  2: 'https://resources1.interface003.com/web30/2026/07/01/b332add6-b673-45c1-8fa2-0544fe873560.png',
  3: 'https://resources1.interface003.com/web30/2026/07/01/ecd458e5-bb11-4a15-a4fb-08bb5849a6e5.png',
}
const AUTOPLAY_INTERVAL = 3000
const STATS_REFRESH_INTERVAL = 40_000
const STATS_ANIMATION_DURATION = 900
const STATS_INCREMENT_DURATION = 3000
const props = defineProps<{
  data?: Record<string, unknown>
}>()

type CountryItem = {
  countryCode?: string
  name?: string
}

const activeBannerIndex = ref(0)
/** 模块容器用于检测动画是否处于真实可视区，不影响接口请求时机。 */
const storeSectionRef = ref<HTMLElement | null>(null)
/** 广告位容器，用于鼠标移出时释放按钮焦点，保证按钮随 hover 结束隐藏。 */
const bannerRef = ref<HTMLElement | null>(null)
/** 客户端加载的排行、广告和统计数据，失败字段继续使用首页聚合数据兜底。 */
const initialStoreData = ref<HomeStoreInitialPayload>({})
const refreshedStatistics = ref<HomeStoreStatistics | null>(null)
const hasStatisticsBaseline = ref(false)
const isStatisticsRefreshing = ref(false)
const animatedStats = reactive<HomeStoreStats>({
  appCount: 0,
  totalDownloads: 0,
  partnerCount: 0,
  visitorCount: 0,
})
const previousStats = reactive<HomeStoreStats>({
  appCount: 0,
  totalDownloads: 0,
  partnerCount: 0,
  visitorCount: 0,
})
const statIncrements = reactive<HomeStoreStats>({
  appCount: 0,
  totalDownloads: 0,
  partnerCount: 0,
  visitorCount: 0,
})
const storeData = computed(() => {
  const payload = mergeHomeStorePayload(props.data, initialStoreData.value)
  return normalizeHomeStoreData(
    refreshedStatistics.value ? mergeHomeStorePayload(payload, { stats: refreshedStatistics.value }) : payload,
  )
})
const hasMonthlyRankings = computed(() => storeData.value.hasMonthlyRankings)
const hasBanners = computed(() => storeData.value.banners.length > 0)
/** 广告超过一条时才提供悬停切换按钮，单条广告保持纯展示。 */
const hasMultipleBanners = computed(() => storeData.value.banners.length > 1)
const cardColumnCount = computed(() => getHomeStoreCardColumnCount(storeData.value.banners))
const cardGridStyle = computed<Record<string, string>>(() => ({
  '--store-card-columns': String(cardColumnCount.value),
}))
const monthlyDownloadApps = computed(() =>
  getVisibleHomeStoreApps(storeData.value.primaryApps, storeData.value.banners),
)
const secondaryApps = computed(() => getVisibleHomeStoreApps(storeData.value.secondaryApps, storeData.value.banners))

const numberFormatter = new Intl.NumberFormat(localeMeta.language)
const statItems = computed(() => [
  {
    key: 'app-count',
    value: animatedStats.appCount,
    increment: statIncrements.appCount,
    label: t('89326'),
  },
  {
    key: 'total-downloads',
    value: animatedStats.totalDownloads,
    increment: statIncrements.totalDownloads,
    label: t('89327'),
  },
  {
    key: 'partner-count',
    value: animatedStats.partnerCount,
    increment: statIncrements.partnerCount,
    label: t('89328'),
  },
  {
    key: 'visitor-count',
    value: animatedStats.visitorCount,
    increment: statIncrements.visitorCount,
    label: t('89329'),
  },
])
const countryGroups = computed(() => (isChineseLocale(locale) ? areaCodeCn : areaCodeEn))
const countries = computed(() => countryGroups.value.flatMap((group) => group.data as CountryItem[]))
const currentCountry = computed(() => {
  return countries.value.find((country) => String(country.countryCode) === String(appStore.CountryCode))
})
const currentCountryName = computed(
  () => currentCountry.value?.name || (isChineseLocale(locale) ? '英国' : 'United Kingdom'),
)
/** Resolve each ranking slot's visible title and destination from the selected data source. */
const primaryTitle = computed(() =>
  storeData.value.primaryRankingKind === 'downloads' ? t('89330') : t('89332', [currentCountryName.value]),
)
const secondaryTitle = computed(() => {
  if (storeData.value.secondaryRankingKind === 'active') return t('89331')

  return t('89332', [currentCountryName.value])
})
const storeHomeHref = computed(() => resolveStoreHref('/'))
const primaryRankingHref = computed(() =>
  resolveStoreSectionHref(storeData.value.primaryRankingKind === 'downloads' ? 'rank' : 'hot'),
)
const secondaryRankingHref = computed(() => resolveStoreSectionHref('hot'))

let bannerTimer: number | undefined
let statisticsTimer: number | undefined
let statsAnimationFrame: number | undefined
let statsIncrementTimer: number | undefined
/** 可视区外暂停数字动画与轮播，接口数据仍正常加载和刷新。 */
let isStoreVisible = false
let storeObserver: IntersectionObserver | undefined
/** 防止初始化请求在组件卸载后返回时重新启动轮询。 */
let isStoreUnmounted = false

watch(
  () => storeData.value.stats,
  () => {
    if (!import.meta.client || !isStoreVisible) return

    updateStatIncrements(storeData.value.stats)
    animateStats()
  },
  { deep: true },
)

watch(
  () => storeData.value.banners.length,
  () => {
    activeBannerIndex.value = 0
    startBannerAutoplay()
  },
)

onMounted(async () => {
  observeStoreVisibility()

  // 随首页在客户端挂载后立即请求 Store 数据，不占用首页 SSR 请求链路。
  const homeStoreDeviceType = getHomeStoreDeviceType(window.navigator.userAgent)
  const payload = await getHomeStoreInitialData(homeStoreDeviceType)
  if (isStoreUnmounted) return

  initialStoreData.value = payload
  startStatisticsPolling()
})

onBeforeUnmount(() => {
  isStoreUnmounted = true
  storeObserver?.disconnect()
  setStoreVisibility(false)
  stopStatisticsPolling()
})

/** 只观察实际视口，不使用懒水合的提前加载范围。 */
function observeStoreVisibility() {
  if (!storeSectionRef.value || !('IntersectionObserver' in window)) {
    setStoreVisibility(true)
    return
  }

  storeObserver = new IntersectionObserver((entries) => {
    const entry = entries[0]
    if (!entry || isStoreUnmounted) return

    setStoreVisibility(entry.isIntersecting && entry.intersectionRatio > 0)
  })
  storeObserver.observe(storeSectionRef.value)
}

/** 进入视口时展示最新统计并启动轮播，离开时取消动画与增量提示。 */
function setStoreVisibility(isVisible: boolean) {
  if (isStoreVisible === isVisible) return
  isStoreVisible = isVisible

  if (isVisible) {
    updateStatIncrements(storeData.value.stats)
    animateStats()
    startBannerAutoplay()
    return
  }

  stopBannerAutoplay()
  if (statsAnimationFrame !== undefined) {
    window.cancelAnimationFrame(statsAnimationFrame)
    statsAnimationFrame = undefined
  }
  clearStatIncrements()
}

/** Resolve Store, current-site and fully external destinations without losing the locale prefix. */
function resolveStoreHref(href: string, linkTarget: HomeStoreLinkTarget = 'store') {
  const normalizedHref = href.trim()

  if (/^https?:\/\//i.test(normalizedHref)) {
    return normalizedHref
  }

  const relativePath = normalizedHref.startsWith('/') ? normalizedHref : `/${normalizedHref}`
  const localizedPath =
    relativePath === '/'
      ? ensureHomeStoreLocalePrefix(localePath('/'), locale)
      : ensureHomeStoreLocalePrefix(relativePath, locale)
  if (linkTarget === 'site') return localizedPath

  return new URL(localizedPath, STORE_ORIGIN).toString()
}

/** Preserve the legacy WikiStore section anchors used by ranking entry links. */
function resolveStoreSectionHref(section: 'rank' | 'hot') {
  const url = new URL(resolveStoreHref('/'))
  url.hash = section
  return url.toString()
}

/** Animate all four metrics together whenever fresh homepage data arrives. */
function animateStats() {
  if (!isStoreVisible) return

  const targets = storeData.value.stats

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    Object.assign(animatedStats, targets)
    return
  }

  if (statsAnimationFrame !== undefined) {
    window.cancelAnimationFrame(statsAnimationFrame)
  }

  const startValues = { ...animatedStats }
  const startedAt = window.performance.now()
  const metricKeys = Object.keys(targets) as Array<keyof HomeStoreStats>

  const updateFrame = (now: number) => {
    const progress = Math.min((now - startedAt) / STATS_ANIMATION_DURATION, 1)
    const easedProgress = 1 - Math.pow(1 - progress, 3)

    for (const key of metricKeys) {
      animatedStats[key] = Math.round(startValues[key] + (targets[key] - startValues[key]) * easedProgress)
    }

    if (progress < 1) {
      statsAnimationFrame = window.requestAnimationFrame(updateFrame)
      return
    }

    statsAnimationFrame = undefined
  }

  statsAnimationFrame = window.requestAnimationFrame(updateFrame)
}

/** Display positive statistic changes beside the animated total briefly. */
function updateStatIncrements(nextStats: HomeStoreStats) {
  const increments = getHomeStoreStatIncrements(previousStats, nextStats, hasStatisticsBaseline.value)

  clearStatIncrements()
  Object.assign(statIncrements, increments)
  Object.assign(previousStats, nextStats)
  hasStatisticsBaseline.value = true

  if (!Object.values(increments).some((increment) => increment > 0)) return

  statsIncrementTimer = window.setTimeout(() => {
    clearStatIncrements()
  }, STATS_INCREMENT_DURATION)
}

/** Cancel any pending increment label and return all increment values to zero. */
function clearStatIncrements() {
  if (statsIncrementTimer !== undefined) {
    window.clearTimeout(statsIncrementTimer)
    statsIncrementTimer = undefined
  }

  Object.assign(statIncrements, {
    appCount: 0,
    totalDownloads: 0,
    partnerCount: 0,
    visitorCount: 0,
  })
}

/** Start the 3-second carousel only when more than one advertisement exists. */
function startBannerAutoplay() {
  stopBannerAutoplay()

  if (!import.meta.client || !isStoreVisible || storeData.value.banners.length <= 1) return

  bannerTimer = window.setInterval(() => {
    activeBannerIndex.value = getNextHomeStoreBannerIndex(activeBannerIndex.value, storeData.value.banners.length)
  }, AUTOPLAY_INTERVAL)
}

function stopBannerAutoplay() {
  if (bannerTimer === undefined) return

  window.clearInterval(bannerTimer)
  bannerTimer = undefined
}

/** 鼠标移出广告位时释放按钮上残留的鼠标焦点，避免按钮不随 hover 结束隐藏。 */
function onBannerMouseLeave() {
  const activeElement = document.activeElement

  if (activeElement instanceof HTMLElement && bannerRef.value?.contains(activeElement)) {
    activeElement.blur()
  }

  startBannerAutoplay()
}

/** 手动切到上一条广告：悬停暂停仍由容器控制，鼠标移出后再恢复自动播放。 */
function goToPrevBanner() {
  activeBannerIndex.value = getPreviousHomeStoreBannerIndex(activeBannerIndex.value, storeData.value.banners.length)
}

/** 手动切到下一条广告：悬停暂停仍由容器控制，鼠标移出后再恢复自动播放。 */
function goToNextBanner() {
  activeBannerIndex.value = getNextHomeStoreBannerIndex(activeBannerIndex.value, storeData.value.banners.length)
}

/** 仅刷新实时统计，保持初始化加载的排行和广告稳定。 */
async function refreshStoreStatistics() {
  if (isStatisticsRefreshing.value) return

  isStatisticsRefreshing.value = true

  try {
    const statistics = await getHomeStoreStatistics()

    if (!isStoreUnmounted && Object.keys(statistics).length > 0) {
      refreshedStatistics.value = statistics
    }
  } catch {
    // Keep the latest verified payload visible when a refresh fails.
  } finally {
    isStatisticsRefreshing.value = false
  }
}

/** 初始化已请求统计数据，之后每 40 秒刷新一次，避免首次重复请求。 */
function startStatisticsPolling() {
  statisticsTimer = window.setInterval(() => void refreshStoreStatistics(), STATS_REFRESH_INTERVAL)
}

/** 组件卸载时清理统计轮询。 */
function stopStatisticsPolling() {
  if (statisticsTimer === undefined) return

  window.clearInterval(statisticsTimer)
  statisticsTimer = undefined
}

/** Keep the carousel paused while keyboard focus remains inside the banner. */
function onBannerFocusOut(event: FocusEvent) {
  const currentTarget = event.currentTarget
  const nextTarget = event.relatedTarget

  if (currentTarget instanceof HTMLElement && nextTarget instanceof Node && currentTarget.contains(nextTarget)) {
    return
  }

  startBannerAutoplay()
}

function formatMetric(value: number) {
  return numberFormatter.format(Math.round(value))
}

function trackStoreMore() {
  matomo.trackEvent('af_Home_Trading_More', {})
}

function trackStoreBanner() {
  matomo.trackEvent('af_Home_Trading_Ads', {})
}

function trackStoreApp(card: HomeStoreApp, rankingKind: 'downloads' | 'popular' | 'active') {
  const trackingType = {
    downloads: '月度下载排行',
    popular: '本地热门应用',
    active: '月度活跃排行',
  }[rankingKind]
  const trackingCode = {
    downloads: '1',
    active: '2',
    popular: '3',
  }[rankingKind]

  matomo.trackEvent('af_Home_Trading_Click', {
    type: trackingType,
    type1: trackingCode,
    name: card.name,
    code: card.id,
  })
}
</script>

<template>
  <section
    v-show="hasMonthlyRankings"
    ref="storeSectionRef"
    class="w-full bg-[#f8f8f8]"
    aria-labelledby="home-wikistore-title"
  >
    <div class="mx-auto flex w-full max-w-[1248px] flex-col gap-[20px] px-[24px] pt-[80px]">
      <div class="flex w-full items-center justify-between">
        <h2 id="home-wikistore-title" class="text-[24px] leading-[32px] font-[600] text-[#292a2e]">
          {{ storeData.title }}
        </h2>
        <NuxtLink
          :to="storeHomeHref"
          external
          target="_blank"
          class="flex size-[32px] cursor-pointer items-center justify-center rounded-[4px] outline-none rtl:rotate-180 hover:[&_.store-arrow_path]:!fill-[#292A2E]"
          aria-label="Open WikiStore"
          @click="trackStoreMore"
        >
          <ArrowRight class="store-arrow size-[24px]" aria-hidden="true" />
        </NuxtLink>
      </div>

      <div
        class="store-layout grid w-full items-stretch gap-[24px]"
        :class="
          hasBanners
            ? 'grid-cols-[258px_334px_minmax(0,1fr)]'
            : 'store-layout--without-banner grid-cols-[258px_minmax(0,1fr)]'
        "
      >
        <NuxtLink
          :to="storeHomeHref"
          external
          target="_blank"
          class="store-stats flex h-[444px] cursor-pointer flex-col justify-between overflow-hidden rounded-[12px] bg-[#112d73] px-[30px] py-[32px] transition-opacity outline-none hover:opacity-95 focus-visible:ring-[2px] focus-visible:ring-[#112d73]/30"
          aria-label="WikiStore statistics"
          @click="trackStoreMore"
        >
          <div v-for="stat in statItems" :key="stat.key" class="flex min-w-0 flex-col gap-[8px]">
            <div class="flex flex-wrap items-baseline gap-x-[4px] gap-y-[0px]" dir="ltr">
              <strong class="store-number-bold text-[32px] leading-[38px] font-[700] text-white">
                {{ formatMetric(stat.value) }}
              </strong>
              <span
                v-if="stat.increment > 0"
                class="store-number inline-flex shrink-0 items-center pb-[2px] text-[14px] leading-[22px] font-[400] text-white"
                aria-live="polite"
              >
                +{{ formatMetric(stat.increment) }}
              </span>
            </div>
            <span class="text-[14px] leading-[22px] font-[400] text-white">{{ stat.label }}</span>
          </div>
        </NuxtLink>

        <div
          v-if="hasBanners"
          ref="bannerRef"
          class="store-banner group relative h-[444px] overflow-hidden rounded-[12px] bg-[#a0a0a0]"
          aria-label="WikiStore featured applications"
          @mouseenter="stopBannerAutoplay"
          @mouseleave="onBannerMouseLeave"
          @focusin="stopBannerAutoplay"
          @focusout="onBannerFocusOut"
        >
          <NuxtLink
            v-for="(banner, index) in storeData.banners"
            :key="banner.id"
            :to="resolveStoreHref(banner.href, banner.linkTarget)"
            external
            target="_blank"
            class="store-banner-slide group absolute inset-0 cursor-pointer outline-none focus-visible:ring-[2px] focus-visible:ring-white focus-visible:ring-inset"
            :class="index === activeBannerIndex ? 'is-active' : ''"
            :aria-hidden="index === activeBannerIndex ? undefined : 'true'"
            :tabindex="index === activeBannerIndex ? 0 : -1"
            :aria-label="banner.title"
            @click="trackStoreBanner"
          >
            <CommonImage
              :src="banner.image"
              :alt="banner.title"
              class="store-banner-image absolute inset-0 size-full scale-[1.02] transition-transform duration-300 ease-out"
              loading="lazy"
              object-fit="cover"
              :fallback-scale="32"
            />
            <span
              class="pointer-events-none absolute inset-x-0 bottom-0 h-[74px] bg-gradient-to-b from-transparent to-black/80"
              aria-hidden="true"
            ></span>
            <span class="absolute inset-x-[22px] top-[385px] flex min-w-0 items-center gap-[8px]">
              <CommonImage
                :src="banner.logo"
                alt=""
                class="size-[32px] shrink-0 overflow-hidden rounded-[6px] border border-white/30"
                loading="lazy"
                object-fit="cover"
                :fallback-scale="60"
              />
              <span class="flex w-[176px] shrink-0 flex-col">
                <span class="truncate text-[14px] leading-[20px] font-[400] text-white">{{ banner.title }}</span>
                <span class="truncate text-[12px] leading-[18px] font-[400] text-white/60">{{ banner.slogan }}</span>
              </span>
            </span>
          </NuxtLink>

          <div
            v-if="hasMultipleBanners"
            class="pointer-events-none absolute inset-0 z-[3] flex items-center justify-between px-[12px]"
          >
            <button
              type="button"
              class="store-banner-nav-btn pointer-events-none flex size-[32px] translate-y-[4px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] text-white opacity-0 backdrop-blur-[10px] transition duration-[250ms] group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:translate-y-0 group-has-[:focus-visible]:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
              aria-label="Previous banner"
              @click.stop.prevent="goToPrevBanner"
            >
              <ArrowRight class="size-[24px] rotate-180 rtl:rotate-0 [&_path]:fill-current" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="store-banner-nav-btn pointer-events-none flex size-[32px] translate-y-[4px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] text-white opacity-0 backdrop-blur-[10px] transition duration-[250ms] group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:translate-y-0 group-has-[:focus-visible]:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
              aria-label="Next banner"
              @click.stop.prevent="goToNextBanner"
            >
              <ArrowRight class="size-[24px] rtl:rotate-180 [&_path]:fill-current" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div class="store-rankings flex min-w-0 flex-col gap-[24px]">
          <div class="flex flex-col gap-[24px]">
            <NuxtLink
              external
              target="_blank"
              class="w-fit truncate text-[16px] leading-[24px] font-[600] text-[#292a2e] hover:opacity-80"
            >
              {{ primaryTitle }}
            </NuxtLink>
            <div class="store-card-grid" :style="cardGridStyle">
              <NuxtLink
                v-for="card in monthlyDownloadApps"
                :key="card.id"
                :to="resolveStoreHref(card.href)"
                external
                target="_blank"
                class="store-card h-[168px] cursor-pointer rounded-[12px] outline-none focus-visible:ring-[2px] focus-visible:ring-[#112d73]/30"
                @click="trackStoreApp(card, storeData.primaryRankingKind)"
              >
                <span class="store-card-inner block size-full">
                  <span
                    class="store-card-face store-card-front flex flex-col items-center justify-center gap-[16px] rounded-[12px] bg-white px-[12px] py-[20px]"
                    aria-hidden="true"
                  >
                    <CommonImage
                      :src="card.logo"
                      :alt="`${card.name} logo`"
                      class="size-[52px] shrink-0 overflow-hidden rounded-[6px] border border-[#051524]/[0.06]"
                      loading="lazy"
                      object-fit="cover"
                      :fallback-scale="62"
                    />
                    <span class="flex w-full min-w-0 flex-col items-center gap-[4px]">
                      <span class="w-full truncate text-center text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                        {{ card.name }}
                      </span>
                      <span class="flex items-center justify-center gap-[4px] text-[#7d818a]">
                        <UserIcon
                          v-if="storeData.primaryRankingKind === 'popular'"
                          class="store-popular-icon size-[14px] shrink-0"
                          aria-hidden="true"
                        />
                        <DownloadIcon v-else class="store-download-icon size-[14px] shrink-0" aria-hidden="true" />
                        <span class="store-number text-[14px] leading-[22px] font-[400]" dir="ltr">
                          {{ formatMetric(card.metricValue) }}
                        </span>
                      </span>
                    </span>
                    <CommonImage
                      v-if="card.showRank !== false && card.rank <= 3"
                      :src="DOWNLOAD_RANK_ICONS[card.rank]"
                      class="store-download-rank-icon absolute top-0 left-0 z-[2] size-[24px] rtl:right-0 rtl:left-auto"
                      alt=""
                      loading="lazy"
                    />
                    <span
                      v-else-if="card.showRank !== false"
                      class="store-rank absolute top-0 left-0 z-[2] flex size-[26px] items-center justify-center rounded-tl-[12px] rounded-br-[12px] text-[12px] leading-none font-[700] rtl:right-0 rtl:left-auto rtl:rounded-tl-none rtl:rounded-tr-[12px] rtl:rounded-br-none rtl:rounded-bl-[12px]"
                      :class="`store-rank--${Math.min(card.rank, 4)}`"
                    >
                      {{ card.rank }}
                    </span>
                  </span>
                  <span
                    class="store-card-face store-card-back overflow-hidden rounded-[12px] bg-[#060606]"
                    aria-hidden="true"
                  >
                    <CommonImage
                      :src="card.coverBackground"
                      :alt="card.name"
                      class="store-card-thumbnail absolute start-[50%] top-[-47px] h-[347px] w-[176px] -translate-x-[50%] rtl:translate-x-[50%]"
                      loading="lazy"
                      object-fit="cover"
                      :fallback-scale="40"
                    />
                    <span class="absolute inset-0 z-[1] bg-black/80"></span>
                    <span
                      v-if="card.showRank !== false && card.rank <= 3"
                      class="store-download-rank-mask absolute top-0 left-0 z-[2] size-[24px] rounded-tl-[12px] rounded-br-[12px] bg-[#060606] rtl:right-0 rtl:left-auto rtl:rounded-tl-none rtl:rounded-tr-[12px] rtl:rounded-br-none rtl:rounded-bl-[12px]"
                      aria-hidden="true"
                    ></span>
                    <CommonImage
                      v-if="card.showRank !== false && card.rank <= 3"
                      :src="DOWNLOAD_RANK_ICONS[card.rank]"
                      class="store-download-rank-icon absolute top-0 left-0 z-[3] size-[24px] rtl:right-0 rtl:left-auto"
                      alt=""
                      loading="lazy"
                    />
                    <span
                      v-else-if="card.showRank !== false"
                      class="store-rank absolute top-0 left-0 z-[2] flex size-[26px] items-center justify-center rounded-tl-[12px] rounded-br-[12px] text-[12px] leading-none font-[700] rtl:right-0 rtl:left-auto rtl:rounded-tl-none rtl:rounded-tr-[12px] rtl:rounded-br-none rtl:rounded-bl-[12px]"
                      :class="`store-rank--${Math.min(card.rank, 4)}`"
                    >
                      {{ card.rank }}
                    </span>
                    <span
                      class="absolute inset-x-[12px] bottom-[12px] z-[2] flex min-w-0 flex-col gap-[2px] text-white"
                    >
                      <strong class="store-number-bold text-[18px] leading-[26px] font-[700]" dir="ltr">
                        {{ formatMetric(card.metricValue) }}
                      </strong>
                      <span class="truncate text-[14px] leading-[22px] font-[400]">{{ card.name }}</span>
                      <span class="store-card-slogan truncate text-[12px] leading-[18px] font-[400] text-white/60">{{
                        card.description
                      }}</span>
                    </span>
                  </span>
                </span>
              </NuxtLink>
            </div>
          </div>

          <div class="flex flex-col gap-[12px]">
            <NuxtLink
              external
              target="_blank"
              class="w-fit truncate text-[16px] leading-[24px] font-[600] text-[#292a2e] hover:opacity-80"
            >
              {{ secondaryTitle }}
            </NuxtLink>
            <div class="store-card-grid" :style="cardGridStyle">
              <NuxtLink
                v-for="card in secondaryApps"
                :key="card.id"
                :to="resolveStoreHref(card.href)"
                external
                target="_blank"
                class="store-card h-[168px] cursor-pointer rounded-[12px] outline-none focus-visible:ring-[2px] focus-visible:ring-[#112d73]/30"
                @click="trackStoreApp(card, storeData.secondaryRankingKind)"
              >
                <span class="store-card-inner block size-full">
                  <span
                    class="store-card-face store-card-front flex flex-col items-center justify-center gap-[16px] rounded-[12px] bg-white px-[12px] py-[20px]"
                    aria-hidden="true"
                  >
                    <CommonImage
                      :src="card.logo"
                      :alt="`${card.name} logo`"
                      class="size-[52px] shrink-0 overflow-hidden rounded-[6px] border border-[#051524]/[0.06]"
                      loading="lazy"
                      object-fit="cover"
                      :fallback-scale="62"
                    />
                    <span class="flex w-full min-w-0 flex-col items-center gap-[4px]">
                      <span class="w-full truncate text-center text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                        {{ card.name }}
                      </span>
                      <span class="flex items-center justify-center gap-[4px] text-[#7d818a]">
                        <UserIcon
                          v-if="storeData.secondaryRankingKind === 'popular'"
                          class="store-popular-icon size-[14px] shrink-0"
                          aria-hidden="true"
                        />
                        <CommonImage
                          v-else
                          :src="ACTIVE_RANKING_ICON"
                          alt=""
                          :width="14"
                          :height="14"
                          loading="lazy"
                          class="size-[14px] shrink-0"
                          aria-hidden="true"
                        />
                        <span class="store-number text-[14px] leading-[22px] font-[400]" dir="ltr">
                          {{ formatMetric(card.metricValue) }}
                        </span>
                      </span>
                    </span>
                    <CommonImage
                      v-if="card.showRank !== false && card.rank <= 3"
                      :src="DOWNLOAD_RANK_ICONS[card.rank]"
                      class="store-download-rank-icon absolute top-0 left-0 z-[2] size-[24px] rtl:right-0 rtl:left-auto"
                      alt=""
                      loading="lazy"
                    />
                    <span
                      v-else-if="card.showRank !== false"
                      class="store-rank absolute top-0 left-0 z-[2] flex size-[26px] items-center justify-center rounded-tl-[12px] rounded-br-[12px] text-[12px] leading-none font-[700] rtl:right-0 rtl:left-auto rtl:rounded-tl-none rtl:rounded-tr-[12px] rtl:rounded-br-none rtl:rounded-bl-[12px]"
                      :class="`store-rank--${Math.min(card.rank, 4)}`"
                    >
                      {{ card.rank }}
                    </span>
                  </span>
                  <span
                    class="store-card-face store-card-back overflow-hidden rounded-[12px] bg-[#060606]"
                    aria-hidden="true"
                  >
                    <CommonImage
                      :src="card.coverBackground"
                      :alt="card.name"
                      class="store-card-thumbnail absolute start-[50%] top-[-47px] h-[347px] w-[176px] -translate-x-[50%] rtl:translate-x-[50%]"
                      loading="lazy"
                      object-fit="cover"
                      :fallback-scale="40"
                    />
                    <span class="absolute inset-0 z-[1] bg-black/80"></span>
                    <CommonImage
                      v-if="card.showRank !== false && card.rank <= 3"
                      :src="DOWNLOAD_RANK_ICONS[card.rank]"
                      class="store-download-rank-icon absolute top-0 left-0 z-[3] size-[24px] rtl:right-0 rtl:left-auto"
                      alt=""
                      loading="lazy"
                    />
                    <span
                      v-else-if="card.showRank !== false"
                      class="store-rank absolute top-0 left-0 z-[2] flex size-[26px] items-center justify-center rounded-tl-[12px] rounded-br-[12px] text-[12px] leading-none font-[700] rtl:right-0 rtl:left-auto rtl:rounded-tl-none rtl:rounded-tr-[12px] rtl:rounded-br-none rtl:rounded-bl-[12px]"
                      :class="`store-rank--${Math.min(card.rank, 4)}`"
                    >
                      {{ card.rank }}
                    </span>
                    <span
                      class="absolute inset-x-[12px] bottom-[12px] z-[2] flex min-w-0 flex-col gap-[2px] text-white"
                    >
                      <strong class="store-number-bold text-[18px] leading-[26px] font-[700]" dir="ltr">
                        {{ formatMetric(card.metricValue) }}
                      </strong>
                      <span class="truncate text-[14px] leading-[22px] font-[400]">{{ card.name }}</span>
                      <span class="store-card-slogan truncate text-[12px] leading-[18px] font-[400] text-white/60">{{
                        card.description
                      }}</span>
                    </span>
                  </span>
                </span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.store-number {
  font-family: WikiFX, Outfit, system-ui;
}

.store-number-bold {
  font-family: WikiFX-Bold, WikiFX, Outfit, system-ui;
}

.store-banner-slide {
  opacity: 0;
  pointer-events: none;
  transition: opacity 300ms ease-in-out;

  &.is-active {
    opacity: 1;
    pointer-events: auto;
  }
}

.store-banner-image {
  :deep(.main-image) {
    object-position: center bottom;
  }
}

.store-card-grid {
  display: grid;
  grid-template-columns: repeat(var(--store-card-columns), minmax(0, 1fr));
  gap: 16px;
}

.store-card {
  perspective: 900px;

  .store-card-inner {
    position: relative;
    transform-style: preserve-3d;
    transition: transform 480ms;
    will-change: transform;
  }

  .store-card-face {
    position: absolute;
    inset: 0;
    clip-path: inset(0 round 14px);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }

  .store-card-back {
    transform: rotateY(180deg);
  }

  &:hover,
  &:focus-visible {
    .store-card-inner {
      transform: rotateY(180deg);
    }
  }
}

.store-card-thumbnail {
  :deep(.main-image) {
    object-position: center top;
  }
}

.store-download-icon {
  :deep(path) {
    fill: #7d818a;
  }
}

.store-rank {
  background: #a6a9b1;
  color: #ffffff;

  &--4 {
    background: #f8f8f8;
    color: #7d818a;
    font-family: WikiFX-Medium, WikiFX, Outfit, system-ui;
    font-size: 14px;
    font-weight: 500;
  }
}

@media screen and (max-width: 1100px) {
  .store-layout:not(.store-layout--without-banner) {
    grid-template-columns: minmax(220px, 258px) minmax(280px, 1fr);

    .store-rankings {
      grid-column: 1 / -1;
    }
  }

  .store-layout--without-banner {
    grid-template-columns: minmax(220px, 258px) minmax(0, 1fr);
  }
}

@media screen and (max-width: 799px) {
  .store-layout,
  .store-layout--without-banner {
    grid-template-columns: minmax(0, 1fr) !important;
  }

  .store-stats {
    display: grid;
    height: auto;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px 24px;
  }

  .store-banner {
    height: min(520px, 130vw);
    min-height: 360px;
  }

  .store-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 520px) {
  .store-stats,
  .store-card-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (prefers-reduced-motion: reduce) {
  .store-banner-slide,
  .store-banner-nav-btn,
  .store-card-inner,
  .store-banner-image {
    transition-duration: 0.01ms !important;
  }
}
</style>
